import { ComponentType, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Dropdown, MenuProps } from 'antd'
import { ONBOARDING_STEPS } from '@forwardflow/constants/onboarding.constants'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useSelectUserOrganizationModalStore } from '@forwardflow/store/modal/SelectUserOrganizationModal'
import { useLogout } from '@forwardflow/hooks/customHooks/onboarding'
import { SaveConflictBanner } from '@forwardflow/modules/onboarding'
import { SelectOperatorOrganization } from '@forwardflow/components'
import { SelectUserOrganizationModal } from '@forwardflow/modals'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import { TOnboardingStep } from '@forwardflow/types/onboarding'
import { IUser } from '@forwardflow/types/user'
import './onboarding-v2.css'
import { SetupFrame } from './frame/SetupFrame'
import { SetupRail } from './rail/SetupRail'
import { Button, Spinner } from './ui/primitives'
import { Icon } from './ui/Icon'
import { ConnectStep } from './steps/ConnectStep'
import { YouStep } from './steps/YouStep'
import { TeamStep } from './steps/TeamStep'
import { CalendarStep } from './steps/CalendarStep'
import { BudgetStep } from './steps/BudgetStep'
import { PaymentStep } from './steps/PaymentStep'
import { AreaStep } from './steps/AreaStep'
import { NumberStep } from './steps/NumberStep'
import {
   StopsDoneStep,
   StopsNeverStep,
   StopsWorkingStep,
} from './steps/StopsStep'
import { BusinessStep } from './steps/BusinessStep'

const STEPS: Partial<Record<TOnboardingStep, ComponentType>> = {
   'fub-connect': ConnectStep,
   you: YouStep,
   'your-team': TeamStep,
   'calendar-connection': CalendarStep,
   budget: BudgetStep,
   payment: PaymentStep,
   'area-code': AreaStep,
   'your-number': NumberStep,
   'stops-working': StopsWorkingStep,
   'stops-done': StopsDoneStep,
   'stops-never': StopsNeverStep,
   'business-details': BusinessStep,
}

/** The account menu, the organization switcher: the same pieces the dashboard header has. */
const HeaderRight = ({
   user,
   currentOrganization,
}: {
   user?: IUser
   currentOrganization?: string | null
}) => {
   const { openSelectUserOrganizationModal } =
      useSelectUserOrganizationModalStore()
   const { logout } = useLogout()
   const isOperator = isOperatorEmail(user?.email || '')
   const hasManyOrgs = (user?.userOrganizations?.length || 0) > 1
   const currentOrganizationObj = user?.userOrganizations?.find(
      (org) => String(org.organizationId) === currentOrganization
   )
   const items: MenuProps['items'] = [
      {
         key: 'logout',
         label: (
            <button
               type="button"
               className="py-1 font-semibold text-[14px] flex items-center gap-2"
               onClick={logout}
               data-testid="ob-logout"
            >
               <Icon name="power" size={15} /> Log out
            </button>
         ),
      },
   ]
   return (
      <>
         {isOperator && <SelectOperatorOrganization />}
         {!isOperator && hasManyOrgs && (
            <>
               <Button
                  variant="secondary"
                  size="sm"
                  onClick={openSelectUserOrganizationModal}
                  className="hidden sm:inline-flex max-w-[200px]"
                  testId="ob-switch-org"
               >
                  <Icon name="building" size={14} />
                  <span className="truncate">
                     {currentOrganizationObj?.organization?.name ||
                        'Organization'}
                  </span>
               </Button>
               <SelectUserOrganizationModal />
            </>
         )}
         <Dropdown menu={{ items }} trigger={['click']}>
            <button
               type="button"
               className="flex items-center justify-center w-10 h-10 rounded-full bg-[var(--ob-surface-2)] hover:bg-[var(--ob-surface-3)] text-[14px] font-semibold"
               aria-label="Account"
               data-testid="ob-account"
            >
               {user?.firstName
                  ? user.firstName[0].toUpperCase()
                  : user?.email
                    ? user.email[0].toUpperCase()
                    : 'F'}
            </button>
         </Dropdown>
      </>
   )
}

const Unavailable = () => {
   const { goToStep, onboardingSaveBlocked } = useOnboardingStoreV2()
   return (
      <div className="h-full flex items-center justify-center px-6">
         <div
            className="max-w-[440px] text-center"
            data-testid="ob-unavailable"
         >
            <h2 className="text-[22px] font-semibold">
               Your saved setup step is unavailable
            </h2>
            <p className="mt-2 text-[14px] text-[var(--ob-text-2)]">
               Your saved answers have not been reset. Reload to check for an
               update, or go back to the first step to continue.
            </p>
            <div className="mt-5 flex items-center justify-center gap-3">
               <Button
                  variant="secondary"
                  onClick={() => window.location.reload()}
               >
                  Reload
               </Button>
               <Button
                  onClick={() => goToStep(ONBOARDING_STEPS[0])}
                  disabled={Boolean(onboardingSaveBlocked)}
               >
                  Start from the first step
               </Button>
            </div>
         </div>
      </div>
   )
}

/**
 * The customer's setup: one question per screen, the rail on the left, the
 * action bar at the bottom. State is the organization's setup record
 * (`useOnboardingStoreV2`), autosaved as it changes; the step order and the
 * skips (the card once paid, the calendar once connected) are the store's.
 * `?step=<id>` opens one screen directly.
 */
export const SetupWizard = ({
   user,
   isLoading,
   currentOrganization,
}: {
   user?: IUser
   isLoading: boolean
   currentOrganization?: string | null
}) => {
   const { getCurrentStep, goToStep, isLoadingOrInitialization } =
      useOnboardingStoreV2()
   const location = useLocation()
   const step = getCurrentStep()

   useEffect(() => {
      const wanted = new URLSearchParams(location.search).get(
         'step'
      ) as TOnboardingStep | null
      if (
         wanted &&
         !isLoading &&
         !isLoadingOrInitialization &&
         ONBOARDING_STEPS.includes(wanted) &&
         wanted !== step
      )
         goToStep(wanted)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [location.search, isLoading, isLoadingOrInitialization])

   const Step = STEPS[step]
   const busy = isLoading || isLoadingOrInitialization

   return (
      <SetupFrame
         rail={<SetupRail />}
         right={
            <HeaderRight
               user={user}
               currentOrganization={currentOrganization}
            />
         }
      >
         <SaveConflictBanner />
         {busy ? (
            <div
               className="h-full flex items-center justify-center text-[var(--ob-text-2)]"
               data-testid="ob-loading"
            >
               <Spinner size={22} />
            </div>
         ) : Step ? (
            <Step />
         ) : (
            <Unavailable />
         )}
      </SetupFrame>
   )
}
